import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';

import {
  IPackagingItem,
  IPackagingItemHistory,
  IPackagingItemListItem,
  PackagingItemStatus,
} from 'src/types/packaging-item';

import axios from '../../utils/axios';

interface IInitialState {
  status: 'idle' | 'loading' | 'success' | 'failure';
  error: string | undefined;
  items: {
    count: number;
    page: number;
    pageSize: number;
    pageCount: number;
    items: IPackagingItemListItem[];
  };
  item: IPackagingItem | null;
  history: {
    status: 'idle' | 'loading' | 'success' | 'failure';
    error: string | undefined;
    data: IPackagingItemHistory | null;
  };
}

const initialState: IInitialState = {
  status: 'idle',
  error: undefined,
  items: {
    count: 0,
    page: 0,
    pageSize: 0,
    pageCount: 0,
    items: [],
  },
  item: null,
  history: {
    status: 'idle',
    error: undefined,
    data: null,
  },
};

const packagingItemSlice = createSlice({
  name: 'packagingItem',
  initialState,
  reducers: {
    removePackagingItem: (state, action) => {
      const { itemId } = action.payload;

      const index = state.items.items.findIndex((item) => item._id === itemId);

      if (index > -1) {
        action.payload.index = index;
        state.items.items = state.items.items.filter((item) => item._id !== itemId);
      }
    },
    rollbackPackagingItemDeletion: (state, action) => {
      const { item, index } = action.payload;

      state.items.items.splice(index, 0, item);
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getPackagingItems.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(getPackagingItems.fulfilled, (state, action) => {
        state.status = 'success';
        state.items = action.payload;
      })
      .addCase(getPackagingItems.rejected, (state, action) => {
        state.items = initialState.items;
        state.status = 'failure';
        state.error = action.error.message;
      })
      .addCase(getPackagingItemById.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(getPackagingItemById.fulfilled, (state, action) => {
        state.status = 'success';
        state.item = action.payload;
        state.error = initialState.error;
      })
      .addCase(getPackagingItemById.rejected, (state, action) => {
        state.item = initialState.item;
        state.status = 'failure';
        state.error = action.error.message;
      })
      .addCase(getPackagingItemHistory.pending, (state) => {
        state.history.status = 'loading';
      })
      .addCase(getPackagingItemHistory.fulfilled, (state, action) => {
        state.history.status = 'success';
        state.history.data = action.payload;
        state.history.error = undefined;
      })
      .addCase(getPackagingItemHistory.rejected, (state, action) => {
        state.history.status = 'failure';
        state.history.error = action.error.message;
      });
  },
});

export default packagingItemSlice.reducer;

export const { removePackagingItem, rollbackPackagingItemDeletion } =
  packagingItemSlice.actions;

export const getPackagingItems = createAsyncThunk(
  'packagingItem/paginated',
  async (params: {
    page: number;
    limit: number;
    generalSearch?: string | null;
    itemCode?: string | null;
    itemName?: string | null;
    minQuantity?: number | null;
    maxQuantity?: number | null;
    units?: string[];
    unitsSeparator?: ',' | '|';
    alertEnabled?: string | null;
    status?: string | null;
    sortBy?: string | null;
    sort?: 'asc' | 'desc' | null;
  }) => {
    const {
      page,
      limit,
      generalSearch,
      itemCode,
      itemName,
      minQuantity,
      maxQuantity,
      units,
      unitsSeparator,
      alertEnabled,
      status,
      sortBy,
      sort,
    } = params;

    try {
      const response = await axios.get(
        `packaging/item?page=${page + 1}&limit=${limit}&generalSearch=${
          generalSearch ?? ''
        }&itemCode=${itemCode ?? ''}&itemName=${itemName ?? ''}&minQuantity=${
          minQuantity ?? ''
        }&maxQuantity=${maxQuantity ?? ''}&unit=${
          units ? units.join(unitsSeparator ?? ',') : ''
        }&alertEnabled=${alertEnabled ?? ''}&status=${status ?? ''}&sortBy=${sortBy}&sort=${
          sort ?? ''
        }`
      );

      return response.data.data;
    } catch (err) {
      console.log(err);
      throw err;
    }
  }
);

export const getPackagingItemById = createAsyncThunk(
  'packagingItem/getById',
  async (params: { itemID: string }) => {
    try {
      const { itemID } = params;
      const response = await axios.get(`packaging/item/${itemID}`);

      return response.data.data;
    } catch (err) {
      console.log(err);
      throw err;
    }
  }
);

export const getPackagingItemHistory = createAsyncThunk(
  'packagingItem/history',
  async (params: { itemID: string }) => {
    try {
      const { itemID } = params;
      const response = await axios.get(`packaging/item/history/${itemID}`);

      return response.data.data;
    } catch (err) {
      console.log(err);
      throw err;
    }
  }
);

export const deletePackagingItem = createAsyncThunk(
  'packagingItem/delete',
  async (params: { item: IPackagingItemListItem; index: number }, { dispatch }) => {
    const { item, index } = params;

    try {
      const response = await axios.delete(`packaging/item/${item._id}`);
      return response.data.data;
    } catch (err) {
      dispatch(rollbackPackagingItemDeletion({ item, index }));
      throw err;
    }
  }
);

export interface TCreatePackagingItem {
  itemName: string;
  initialQuantity?: number | null;
  suppliers?: string[];
  unit: string;
  // Linked product id; null unlinks on update.
  product?: string | null;
  reorderThreshold?: number | null;
  alertEnabled?: boolean;
}

export interface TUpdatePackagingItem extends Omit<TCreatePackagingItem, 'initialQuantity'> {
  status?: PackagingItemStatus;
}

export const createPackagingItem = createAsyncThunk(
  'packagingItem/create',
  async (params: { newItem: TCreatePackagingItem }) => {
    const { newItem } = params;
    const response = await axios.post('packaging/item', newItem);
    return response.data.data;
  }
);

export const updatePackagingItem = createAsyncThunk(
  'packagingItem/update',
  async (params: { itemId: string; updatingItem: TUpdatePackagingItem }) => {
    const { itemId, updatingItem } = params;
    const response = await axios.put(`packaging/item/${itemId}`, updatingItem);
    return response.data.data;
  }
);

export const adjustPackagingItemQuantity = createAsyncThunk(
  'packagingItem/adjustQuantity',
  async (params: { itemId: string; newQuantity: number; reason?: string }) => {
    const { itemId, newQuantity, reason } = params;
    const response = await axios.patch(`packaging/item/${itemId}/adjust-quantity`, {
      newQuantity,
      reason,
    });
    return response.data.data;
  }
);
